import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { cn } from '../../../../../lib/utils';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../components/ui/card';
import { DateRangePicker } from '../../../../../components/ui/date-range-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { Label } from '../../../../../components/ui/label';
import { CollapsibleFilterBar } from '../materials/tabs/Filters/CollapsibleFilterBar';
import type { AuditTrailItem } from './auditTrailTypes';
import type { AuditSessionGroup } from '../../../../audit/auditSessionGrouping';
import {
  useAuditTrailTimelineQuery,
  type AuditTimelineFilters,
} from './useAuditTrailTimelineQuery';
import { useAuditTrailSessionEvents } from './useAuditTrailSessionEvents';
import {
  getProjectDisplayName,
  getTimelineEventBadge,
  getTimelineMetaModule,
  getTimelineSummary,
} from './auditTrailConstants';
import { getCurrentAuditActorEmail } from '../../../../audit/auditCurrentUser';
import {
  formatAuditTrailDateOnly,
  formatAuditTrailTimeOnly,
  formatAuditTrailTimeWithSeconds,
} from '../../../../audit/auditTrailDateTime';
import {
  AuditEmptyState,
  AuditErrorBanner,
  AuditLoadingState,
} from './auditTrailUi';

interface UserActivityTimelineViewProps {
  projectId?: number;
  filters: AuditTimelineFilters;
  updateFilters: (patch: Partial<AuditTimelineFilters>) => void;
  onOpenDetail: (row: AuditTrailItem) => void;
}

function toTitleCase(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatSessionIdDisplay(sessionId: string): string {
  if (!sessionId || sessionId === '—') return '—';
  const clean = sessionId.replace(/-/g, '');
  if (clean.length >= 6) return `S-${clean.slice(0, 6)}`;
  return sessionId.length > 12 ? `${sessionId.slice(0, 12)}…` : sessionId;
}

function timelineDotClass(severity: string): string {
  const s = severity?.toUpperCase() ?? 'INFO';
  if (s === 'ERR' || s === 'ERROR') return 'border-readable-red';
  if (s === 'WARN' || s === 'WARNING') return 'border-[#B38600]';
  return 'border-blueprint-blue';
}

function timelineTypeClass(severity: string): string {
  const s = severity?.toUpperCase() ?? 'INFO';
  if (s === 'ERR' || s === 'ERROR') return 'text-readable-red';
  return 'text-blueprint-blue';
}

function actorHeaderName(label: string, secondary?: string): string {
  if (secondary?.trim()) return toTitleCase(secondary.trim());
  if (label.includes('@')) {
    const local = label.split('@')[0].replace(/[._]/g, ' ');
    return toTitleCase(local);
  }
  return toTitleCase(label);
}

function TimelineEventItem({
  row,
  onOpenDetail,
}: {
  row: AuditTrailItem;
  onOpenDetail: (row: AuditTrailItem) => void;
}) {
  const project = getProjectDisplayName(row);
  const metaProject = project && project !== '—' ? project : 'System';

  return (
    <div
      className="relative mb-[18px] pl-2 cursor-pointer group"
      onClick={() => onOpenDetail(row)}
      onKeyDown={(e) => e.key === 'Enter' && onOpenDetail(row)}
      role="button"
      tabIndex={0}
    >
      <div
        className={cn(
          'absolute -left-[20px] top-1.5 h-3 w-3 rounded-full bg-white border-[3px] box-border',
          timelineDotClass(row.severity)
        )}
      />
      <div className="text-[11px] font-semibold text-heritage-grey tabular-nums">
        {formatAuditTrailTimeWithSeconds(row.created_at)}
      </div>
      <div className="text-[13px] font-medium text-text-grey mt-0.5 group-hover:text-blueprint-blue transition-colors">
        {getTimelineSummary(row)}
      </div>
      <div className="text-[12px] text-heritage-grey mt-1">
        {getTimelineMetaModule(row.module)} · {metaProject}
      </div>
      <div
        className={cn(
          'text-[10px] font-bold uppercase tracking-[0.05em] mt-1',
          timelineTypeClass(row.severity)
        )}
      >
        {getTimelineEventBadge(row.event_type)}
      </div>
    </div>
  );
}

function SessionTimelinePanel({
  projectId,
  actorLabel,
  actorSecondary,
  session,
  onOpenDetail,
}: {
  projectId?: number;
  actorLabel: string;
  actorSecondary?: string;
  session: AuditSessionGroup;
  onOpenDetail: (row: AuditTrailItem) => void;
}) {
  const { events, loading, error } = useAuditTrailSessionEvents(projectId, session.sessionId);
  const displayEvents = events.length > 0 ? events : session.events;
  const displayName = actorHeaderName(actorLabel, actorSecondary);
  const sessionShort = formatSessionIdDisplay(session.sessionId);
  const dateLabel = formatAuditTrailDateOnly(session.startAt);
  const endAt = displayEvents[displayEvents.length - 1]?.created_at ?? session.endAt;

  return (
    <Card className="border-[#E2E5EA] shadow-none lg:max-h-[620px] flex flex-col min-h-0">
      <CardContent className="p-5 md:p-6 flex flex-col min-h-0 flex-1">
        <div className="border-b border-[#EEF0F3] pb-4 mb-5 shrink-0">
          <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">
            {displayName} — session {sessionShort}
          </h3>
          <p className="text-[13px] text-heritage-grey mt-1">
            {dateLabel} · {formatAuditTrailTimeOnly(session.startAt)} to {formatAuditTrailTimeOnly(endAt)}
          </p>
        </div>

        {error ? (
          <AuditErrorBanner message={error} />
        ) : loading && displayEvents.length === 0 ? (
          <AuditLoadingState message="Loading session events…" />
        ) : displayEvents.length === 0 ? (
          <AuditEmptyState title="No events in this session" />
        ) : (
          <div className="relative pl-7 overflow-y-auto flex-1 min-h-0 pr-1">
            <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-[#E2E5EA]" />
            {displayEvents.map((row) => (
              <TimelineEventItem key={row.id} row={row} onOpenDetail={onOpenDetail} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const UserActivityTimelineView: React.FC<UserActivityTimelineViewProps> = ({
  projectId,
  filters,
  updateFilters,
  onOpenDetail,
}) => {
  const { actorGroups, sessionCount, total, loading, error, isActorFiltered, isDateFiltered } =
    useAuditTrailTimelineQuery(projectId, filters);

  const [selectedActorKey, setSelectedActorKey] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const selectedActor = useMemo(
    () => actorGroups.find((g) => g.actorKey === selectedActorKey) ?? actorGroups[0] ?? null,
    [actorGroups, selectedActorKey]
  );

  const sessions = selectedActor?.sessions ?? [];

  const selectedSession = useMemo(
    () => sessions.find((s) => s.sessionId === selectedSessionId) ?? sessions[0] ?? null,
    [sessions, selectedSessionId]
  );

  useEffect(() => {
    if (actorGroups.length === 0) {
      setSelectedActorKey(null);
      setSelectedSessionId(null);
      return;
    }
    setSelectedActorKey((prev) => {
      if (prev && actorGroups.some((g) => g.actorKey === prev)) return prev;
      return actorGroups[0].actorKey;
    });
  }, [actorGroups]);

  useEffect(() => {
    if (sessions.length === 0) {
      setSelectedSessionId(null);
      return;
    }
    setSelectedSessionId((prev) => {
      if (prev && sessions.some((s) => s.sessionId === prev)) return prev;
      return sessions[0].sessionId;
    });
  }, [sessions, selectedActorKey]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.actorId.trim()) n += 1;
    if (filters.dateFrom || filters.dateTo) n += 1;
    return n;
  }, [filters.actorId, filters.dateFrom, filters.dateTo]);

  const handleDateRangeUpdate = useCallback(
    (values: { dateFrom: Date | undefined; dateTo: Date | undefined }) => {
      updateFilters({ dateFrom: values.dateFrom, dateTo: values.dateTo });
    },
    [updateFilters]
  );

  const clearFilters = useCallback(() => {
    updateFilters({ actorId: '', dateFrom: undefined, dateTo: undefined });
  }, [updateFilters]);

  const applyMyActivity = useCallback(() => {
    const email = getCurrentAuditActorEmail();
    if (email) updateFilters({ actorId: email });
  }, [updateFilters]);

  const applyToday = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);
    updateFilters({ dateFrom: today, dateTo: end, actorId: '' });
  }, [updateFilters]);

  const actorHeader = selectedActor
    ? actorHeaderName(selectedActor.actorLabel, selectedActor.actorSecondary).toUpperCase()
    : '';

  return (
    <div className="space-y-4">
      <CollapsibleFilterBar
        searchQuery={filters.actorId}
        onSearchChange={(value) => updateFilters({ actorId: value })}
        searchPlaceholder="Filter by actor name or email (leave empty for all users)…"
        activeFilterCount={activeFilterCount}
        filteredCount={actorGroups.length}
        filteredCountLabel="users"
        onResetFilters={clearFilters}
      >
        <div className="flex items-center gap-2 flex-wrap w-full pb-1">
          <Button type="button" variant="outline" size="sm" className="h-8 text-[12px]" onClick={applyMyActivity}>
            My activity
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8 text-[12px]" onClick={applyToday}>
            Today
          </Button>
        </div>
        <DateRangePicker
          initialDateFrom={filters.dateFrom}
          initialDateTo={filters.dateTo}
          onUpdate={handleDateRangeUpdate}
          showCompare={false}
        />
      </CollapsibleFilterBar>

      <p className="text-[12px] text-heritage-grey px-1">
        {isDateFiltered
          && `${total} events · ${sessionCount} sessions · ${actorGroups.length} users`
        }
      </p>

      {error ? <AuditErrorBanner message={error} /> : null}

      {loading && actorGroups.length === 0 ? (
        <Card className="border-[#E2E5EA] shadow-none">
          <CardContent className="p-6">
            <AuditLoadingState message="Loading user activity…" />
          </CardContent>
        </Card>
      ) : actorGroups.length === 0 ? (
        <Card className="border-[#E2E5EA] shadow-none">
          <CardContent className="p-6">
            <AuditEmptyState
              title="No activity found"
              description="Try Today, clear filters, or select All projects on Admin."
            />
          </CardContent>
        </Card>
      ) : (
        <>
          {actorGroups.length > 1 ? (
            <div className="flex flex-col gap-1 max-w-md">
              <Label className="text-[11px] text-heritage-grey">User</Label>
              <Select value={selectedActor?.actorKey ?? ''} onValueChange={setSelectedActorKey}>
                <SelectTrigger className="h-9 text-[13px]">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {actorGroups.map((actor) => (
                    <SelectItem key={actor.actorKey} value={actor.actorKey}>
                      {actor.actorLabel}
                      {actor.actorSecondary ? ` (${actor.actorSecondary})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 items-stretch">
            <div className="rounded-lg border border-[#E2E5EA] bg-white p-3 lg:max-h-[620px] flex flex-col min-h-0">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-heritage-grey mb-2.5 px-1 shrink-0">
                Sessions — {actorHeader}
              </h3>
              <div className="overflow-y-auto flex-1 -mx-1 px-1">
                {sessions.map((session) => {
                  const isActive = selectedSession?.sessionId === session.sessionId;
                  const sessionShort = formatSessionIdDisplay(session.sessionId);
                  return (
                    <button
                      key={session.sessionId}
                      type="button"
                      onClick={() => setSelectedSessionId(session.sessionId)}
                      className={cn(
                        'w-full text-left rounded-lg p-3 border transition-colors mb-1',
                        isActive
                          ? 'bg-[rgba(0,39,63,0.06)] border-blueprint-blue'
                          : 'border-transparent hover:bg-page-bg'
                      )}
                    >
                      <div className="text-[12px] font-semibold text-blueprint-blue leading-snug">
                        {formatAuditTrailDateOnly(session.startAt)} · {formatAuditTrailTimeOnly(session.startAt)} –{' '}
                        {formatAuditTrailTimeOnly(session.endAt)}
                      </div>
                      <div className="text-[11px] text-heritage-grey mt-1">Session {sessionShort}</div>
                      <div className="text-[11px] font-semibold text-hero-red mt-1.5">
                        {session.events.length} events
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedSession && selectedActor ? (
              <SessionTimelinePanel
                key={selectedSession.sessionId}
                projectId={projectId}
                actorLabel={selectedActor.actorLabel}
                actorSecondary={selectedActor.actorSecondary}
                session={selectedSession}
                onOpenDetail={onOpenDetail}
              />
            ) : (
              <Card className="border-[#E2E5EA] shadow-none">
                <CardContent className="p-6">
                  <AuditEmptyState
                    title="Select a session"
                    description="Choose a session from the list to view all events."
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};
